'use strict';

const get = require('lodash.get');
const { URL } = require('bunsen/helpers/uri');

const { fetchWrapper } = require('dibs-service-fetch-wrappers/dist/util');
const GAC = require('./galleryActionConstants');

// Stores
const GalleryStore = require('../stores/GalleryStore.es');
const CreatorsStore = require('../../global/stores/CreatorsStore');

// Actions
const shareActions = require('../../global/actions/shareActions');
const galleryPhotoUIActions = require('./galleryPhotoUIActions');
const galleryTrackingActions = require('./galleryTrackingActions');
const creatorsActions = require('../../global/actions/creatorsActions');

/**
 * Sets up the store with photos from a single project and firm
 * @param ac
 * @param payload
 * @param done
 */
function setupStoreWithSingleProjectPhotos(ac, payload, done) {
    ac.dispatch(GAC.GALLERY_SETUP_STORE, {
        galleryPhotos: payload.photos,
        project: {
            name: payload.projectName,
            url: payload.projectUrl,
            location: payload.projectLocation,
        },
        firm: {
            name: payload.firmName,
            url: payload.firmUrl,
            photo: payload.firmPhoto,
            urlLabel: payload.firmUrlLabel,
        },
        initialPhoto: payload.photo,
        totalPhotos: (payload.photos || []).length,
    });
    done();
}

/**
 * Sets up the store with photos from multiple projects and firms
 * @param ac
 * @param payload
 * @param done
 */
function setupStoreWithMultipleProjectPhotos(ac, payload, done) {
    ac.dispatch(GAC.GALLERY_SETUP_STORE, payload);
    done();
}

function resetSetupStore(ac, payload, done) {
    ac.dispatch(GAC.GALLERY_RESET_STORE, payload);
    done();
}

function addSimilarItemsTemplate(ac, payload, done) {
    ac.dispatch(GAC.GALLERY_ADD_SIMILAR_ITEMS_TEMPLATE, payload);
    done();
}

function addMoreProjectsTemplate(ac, payload, done) {
    ac.dispatch(GAC.GALLERY_ADD_MORE_PROJECTS_TEMPLATE, payload);
    done();
}

function fetchItemTagCreators(ac, payload, done) {
    const creatorsStore = ac.getStore(CreatorsStore);
    const itemTags = ac.getStore(GalleryStore).getCurrentPhotoItemTags();

    const creatorIds = itemTags.reduce((_creatorIds, itemTag) => {
        const creatorId = get(itemTag, 'attributeProfile.dataMap.creatorId[0].value', '');

        if (!!creatorId && !creatorsStore.getCreatorById(creatorId)) {
            _creatorIds.push(creatorId);
        }

        return _creatorIds;
    }, []);

    if (creatorIds.length) {
        ac.executeAction(creatorsActions.fetchCreators, { creatorIds });
    }

    done();
}

function fetchSimilarItemsTemplate(ac, payload, done) {
    const galleryStore = ac.getStore(GalleryStore);

    // Check the store if similar items should be fetched
    if (galleryStore.shouldFetchSimilarItems()) {
        // Fetch Similar Items Template
        fetchWrapper(galleryStore.getSimilarItemsFullUrl())
            .then(response => {
                // Add Template to Gallery Store
                ac.executeAction(addSimilarItemsTemplate, response);
                done();
            })
            .catch(done);
        return;
    }

    done();
}

function fetchMoreProjectsTemplate(ac, payload, done) {
    const galleryStore = ac.getStore(GalleryStore);

    // Check the store if more projects should be fetched
    if (galleryStore.shouldFetchMoreProjects()) {
        // Fetch More Projects Template
        fetchWrapper(galleryStore.getSimilarItemsFullUrl())
            .then(response => {
                // Add Template to Gallery Store
                ac.executeAction(addMoreProjectsTemplate, response);
                done();
            })
            .catch(done);
        return;
    }

    done();
}

/**
 * Action for loading gallery photos asynchronously
 * @param ac
 * @param payload
 * @param done
 */
function loadPhotos(ac, payload, done) {
    const galleryStore = ac.getStore(GalleryStore);

    if (!galleryStore.shouldLoadPhotos()) {
        done();
    } else {
        ac.dispatch(GAC.GALLERY_PHOTOS_LOAD_START);

        fetchWrapper(galleryStore.getLoadPhotosFullUrl())
            .then(response => {
                ac.dispatch(GAC.GALLERY_PHOTOS_LOAD_DONE, response);
                done();
            })
            .catch(error => {
                ac.dispatch(GAC.GALLERY_PHOTOS_LOAD_FAILED, { error });
                done();
            });
    }
}

/**
 * Action for setting the gallery's photo to a particular photo by its id
 * @param ac
 * @param payload
 * @param done
 */
function setPhoto(ac, payload, done) {
    ac.dispatch(GAC.GALLERY_SET_PHOTO, payload);

    ac.executeAction(fetchItemTagCreators);
    ac.executeAction(loadPhotos);
    ac.executeAction(galleryPhotoUIActions.flashItemTagIndicators);

    ac.executeAction(fetchSimilarItemsTemplate, payload, () => {
        ac.executeAction(galleryTrackingActions.fireVPVAndImpressions);
        done();
    });

    ac.executeAction(fetchMoreProjectsTemplate);
}

/**
 * Action for switching the current photo (Next, Previous...)
 * @param ac
 * @param payload
 * @param done
 */
function switchPhoto(ac, payload, done) {
    ac.dispatch(GAC.GALLERY_SWITCH_PHOTO, payload);

    ac.executeAction(fetchItemTagCreators);
    ac.executeAction(loadPhotos);
    ac.executeAction(galleryPhotoUIActions.flashItemTagIndicators);

    // Switch Photo Tracking Actions
    // (Note: ordering of these tracking events is important
    //      for making sure events are associated with correct pages)
    ac.executeAction(galleryTrackingActions.trackPhotoSwitch, payload);
    ac.executeAction(fetchSimilarItemsTemplate, payload, () => {
        ac.executeAction(galleryTrackingActions.fireVPVAndImpressions);
        done();
    });

    ac.executeAction(fetchMoreProjectsTemplate);
}

/**
 * Action to show details on mobile
 */
function showDetailsMobile(ac, payload, done) {
    ac.dispatch(GAC.GALLERY_MOBILE_SHOW_DETAILS, payload);
    done();
}

function toggleDetailsMobile(ac, payload, done) {
    const showDetails = !ac.getStore(GalleryStore).showDetailsMobile();
    ac.executeAction(showDetailsMobile, { showDetails });
    done();
}

function toggleSocialMobile(ac, payload, done) {
    const showSocial = !ac.getStore(GalleryStore).showSocialMobile();
    ac.dispatch(GAC.GALLERY_MOBILE_SHOW_SOCIAL, { showSocial });
    done();
}

/**
 * Action for overwriting the gallery's default copy
 * @see '/json/copy/photoGallery.json'
 */
function updateCopy(ac, payload, done) {
    ac.dispatch(GAC.GALLERY_UPDATE_COPY, payload);
    done();
}

/**
 * Action for sharing the current photo
 *
 * @param ac
 * @param {string} type
 * @param {function} done
 */
function sharePhoto(ac, { type }, done) {
    const url = URL.host + URL.path;
    const galleryStore = ac.getStore(GalleryStore);

    if (type && shareActions.types.indexOf(type) > -1) {
        ac.executeAction(shareActions.shareContent, {
            url,
            type,
            copy: galleryStore.getGalleryCopy('socialSharing'),
            image: galleryStore.getCurrentPhotoSrc(),
            tokens: {
                url,
                firmName: galleryStore.getPhotoFirm('name'),
                projectName: galleryStore.getPhotoProject('name'),
            },
            tracking: {
                page: 'trade firm project image',
                shared: 'trade firm project image',
            },
        });
    }

    done();
}

module.exports = {
    setupStoreWithSingleProjectPhotos,
    setupStoreWithMultipleProjectPhotos,
    resetSetupStore,
    switchPhoto,
    setPhoto,
    loadPhotos,
    updateCopy,
    fetchItemTagCreators,
    showDetailsMobile,
    toggleSocialMobile,
    toggleDetailsMobile,
    addSimilarItemsTemplate,
    addMoreProjectsTemplate,
    fetchSimilarItemsTemplate,
    fetchMoreProjectsTemplate,
    sharePhoto,
};



// WEBPACK FOOTER //
// ./assets/js/gallery/actions/galleryActions.es.js